import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/components/FormError';
import { ApiRequestError, apiFetch } from '@/lib/api/client';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';

import {
  type NotFoundRow,
  REDIRECT_SOURCES,
  type RedirectInput,
  type RedirectRow,
  type RedirectSource,
} from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.8 — `/redirects`, both tabs.
 *
 * "**Redirects** (searchable list with hit counts, sorted by most-hit, inline
 * add/edit) and **Unmatched 404s** … After launch this is the tool that
 * recovers lost SEO traffic — build it properly."
 *
 * The two tabs are one screen because the workflow crosses between them: an
 * editor reads a path off the 404 list, decides where it should go, and writes
 * the redirect. "Create redirect to…" carries the path across rather than
 * asking them to copy a percent-encoded Georgian URL by hand.
 */
export function RedirectsPage() {
  const { t } = useTranslation();

  const [tab, setTab] = useState<'redirects' | 'not-found'>('redirects');
  const [draft, setDraft] = useState<Partial<RedirectInput> | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.redirects')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('redirects.intro')}</p>
      </div>

      <nav aria-label={t('redirects.tabs')}>
        <ul className="flex flex-wrap gap-2">
          {(['redirects', 'not-found'] as const).map((option) => (
            <li key={option}>
              <button
                type="button"
                aria-current={tab === option ? 'page' : undefined}
                onClick={() => setTab(option)}
                className={[
                  'inline-flex rounded-full px-4 py-2 text-body-sm font-semibold transition-colors',
                  tab === option
                    ? 'bg-navy-600 text-white'
                    : 'bg-surface-subtle text-secondary hover:text-primary',
                ].join(' ')}
              >
                {t(`redirects.tab.${option}`)}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {tab === 'redirects' ? (
        <RedirectList draft={draft} onDraftChange={setDraft} />
      ) : (
        <NotFoundList
          onRedirect={(path) => {
            // Carried across, and the tab switched: the editor is mid-decision
            // and retyping the path is where the mistake would come from.
            setDraft({ from_path: path, source: 'manual', status_code: 301 });
            setTab('redirects');
          }}
        />
      )}
    </div>
  );
}

function RedirectList({
  draft,
  onDraftChange,
}: {
  draft: Partial<RedirectInput> | null;
  onDraftChange: (draft: Partial<RedirectInput> | null) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<RedirectRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const redirects = useQuery({
    queryKey: ['admin', 'redirects', search],
    queryFn: () =>
      apiFetch<{ data: RedirectRow[] }>('/admin/redirects', {
        query: { search: search.trim() === '' ? undefined : search.trim(), per_page: 50 },
      }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => apiFetch(`/admin/redirects/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'redirects'] }),
    onError: (cause: unknown) =>
      setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic')),
  });

  return (
    <div className="space-y-4">
      <FormError message={error} />

      <div className="flex flex-wrap items-end gap-3">
        <Input
          label={t('list.search')}
          hideLabel
          placeholder={t('redirects.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <Button
          type="button"
          size="sm"
          iconLeft={<Plus size={16} strokeWidth={1.75} aria-hidden="true" />}
          onClick={() => onDraftChange({ source: 'manual', status_code: 301 })}
        >
          {t('redirects.new')}
        </Button>
      </div>

      {draft !== null || editing !== null ? (
        <RedirectForm
          key={editing?.id ?? 'new'}
          redirect={editing}
          draft={draft}
          onDone={() => {
            setEditing(null);
            onDraftChange(null);
            void queryClient.invalidateQueries({ queryKey: ['admin', 'redirects'] });
          }}
          onCancel={() => {
            setEditing(null);
            onDraftChange(null);
          }}
        />
      ) : null}

      {redirects.isPending ? <Spinner size="lg" label={t('common.loading')} /> : null}

      <div className="overflow-x-auto rounded-card border border-subtle">
        <table className="w-full text-body-sm">
          <caption className="sr-only">{t('redirects.tab.redirects')}</caption>

          <thead className="bg-surface-subtle border-b border-subtle">
            <tr>
              <th scope="col" className="p-3 text-start font-extrabold text-primary">
                {t('redirects.columns.from')}
              </th>
              <th scope="col" className="p-3 text-start font-extrabold text-primary">
                {t('redirects.columns.to')}
              </th>
              <th scope="col" className="p-3 text-start font-extrabold text-primary">
                {t('redirects.columns.source')}
              </th>
              <th scope="col" className="p-3 text-end font-extrabold text-primary">
                {t('redirects.columns.hits')}
              </th>
              <th scope="col" className="p-3 text-end font-extrabold text-primary">
                {t('list.actions')}
              </th>
            </tr>
          </thead>

          <tbody>
            {(redirects.data?.data ?? []).map((row) => (
              <tr key={row.id} className="border-b border-subtle last:border-0">
                <td className="max-w-64 p-3">
                  <button
                    type="button"
                    className="truncate text-start font-semibold text-primary underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
                    onClick={() => setEditing(row)}
                  >
                    {row.from_path}
                  </button>
                </td>

                <td className="max-w-64 p-3">
                  <span className="flex items-center gap-1.5 text-secondary">
                    <ArrowRight
                      size={14}
                      strokeWidth={1.75}
                      aria-hidden="true"
                      className="shrink-0 text-muted"
                    />
                    <span className="truncate">{row.to_path}</span>
                  </span>
                </td>

                <td className="p-3">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <Chip tone="neutral">{t(`redirects.sources.${row.source}`)}</Chip>
                    {/* 301 is permanent and is what recovers the ranking; a
                        302 is worth seeing at a glance because it does not. */}
                    {row.status_code === 301 ? null : (
                      <Chip tone="gold">{String(row.status_code)}</Chip>
                    )}
                  </span>
                </td>

                <td className="p-3 text-end">
                  <span className="tabular-nums text-primary">{row.hit_count}</span>
                  {row.last_hit_at ? (
                    <span className="block text-caption text-muted">
                      {new Date(row.last_hit_at).toLocaleDateString(locale)}
                    </span>
                  ) : null}
                </td>

                <td className="p-3 text-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t('redirects.remove', { path: row.from_path })}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(row.id)}
                  >
                    <Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />
                  </Button>
                </td>
              </tr>
            ))}

            {redirects.data?.data.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-4 text-body-sm text-secondary">
                  {t('redirects.empty')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** §5.8's "inline add/edit". */
function RedirectForm({
  redirect,
  draft,
  onDone,
  onCancel,
}: {
  redirect: RedirectRow | null;
  draft: Partial<RedirectInput> | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  const [from, setFrom] = useState(redirect?.from_path ?? draft?.from_path ?? '');
  const [to, setTo] = useState(redirect?.to_path ?? draft?.to_path ?? '');
  const [status, setStatus] = useState(redirect?.status_code ?? draft?.status_code ?? 301);
  const [source, setSource] = useState<RedirectSource>(
    redirect?.source ?? draft?.source ?? 'manual',
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiFetch(redirect === null ? '/admin/redirects' : `/admin/redirects/${redirect.id}`, {
        method: redirect === null ? 'POST' : 'PUT',
        body: { from_path: from.trim(), to_path: to.trim(), status_code: status, source },
      }),
    onSuccess: onDone,
    onError: (cause: unknown) => {
      if (cause instanceof ApiRequestError) {
        setErrors(
          Object.fromEntries(
            Object.entries(cause.validationErrors).map(([field, messages]) => [
              field,
              messages[0] ?? '',
            ]),
          ),
        );
      }

      setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic'));
    },
  });

  return (
    <form
      className="space-y-4 rounded-card border border-navy-600 p-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        setErrors({});
        save.mutate();
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-h4 font-extrabold text-primary">
          {redirect === null ? t('redirects.new') : t('redirects.edit')}
        </h2>

        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label={t('common.cancel')}
          onClick={onCancel}
        >
          <X size={16} strokeWidth={1.75} aria-hidden="true" />
        </Button>
      </div>

      <FormError message={error} />

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('redirects.columns.from')}
          hint={t('redirects.fields.fromHint')}
          value={from}
          onChange={(event) => setFrom(event.target.value)}
          error={errors.from_path}
        />

        <Input
          label={t('redirects.columns.to')}
          hint={t('redirects.fields.toHint')}
          value={to}
          onChange={(event) => setTo(event.target.value)}
          error={errors.to_path}
        />

        <Select
          label={t('redirects.fields.statusCode')}
          hint={t('redirects.fields.statusCodeHint')}
          value={String(status)}
          onChange={(event) => setStatus(Number(event.target.value))}
          options={[301, 302, 307, 308].map((code) => ({
            value: String(code),
            label: t(`redirects.codes.${code}`, String(code)),
          }))}
        />

        <Select
          label={t('redirects.columns.source')}
          value={source}
          onChange={(event) => setSource(event.target.value as RedirectSource)}
          options={REDIRECT_SOURCES.map((option) => ({
            value: option,
            label: t(`redirects.sources.${option}`),
          }))}
        />
      </div>

      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? t('common.saving') : t('common.save')}
      </Button>
    </form>
  );
}

/** §5.8's second tab. */
function NotFoundList({ onRedirect }: { onRedirect: (path: string) => void }) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const queryClient = useQueryClient();

  const [days, setDays] = useState(30);

  const hits = useQuery({
    queryKey: ['admin', 'not-found', days],
    queryFn: () =>
      apiFetch<{ data: NotFoundRow[] }>('/admin/redirects/not-found', {
        query: { days, per_page: 50 },
      }),
  });

  const dismiss = useMutation({
    mutationFn: (id: number) => apiFetch(`/admin/redirects/not-found/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'not-found'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="max-w-prose text-body-sm text-secondary">{t('redirects.notFoundIntro')}</p>

        <Select
          label={t('redirects.period')}
          value={String(days)}
          onChange={(event) => setDays(Number(event.target.value))}
          options={[7, 30, 90, 365].map((option) => ({
            value: String(option),
            label: t('redirects.lastDays', { count: option }),
          }))}
        />
      </div>

      {hits.isPending ? <Spinner size="lg" label={t('common.loading')} /> : null}

      <div className="overflow-x-auto rounded-card border border-subtle">
        <table className="w-full text-body-sm">
          <caption className="sr-only">{t('redirects.tab.not-found')}</caption>

          <thead className="bg-surface-subtle border-b border-subtle">
            <tr>
              <th scope="col" className="p-3 text-start font-extrabold text-primary">
                {t('redirects.columns.path')}
              </th>
              <th scope="col" className="p-3 text-start font-extrabold text-primary">
                {t('redirects.columns.referer')}
              </th>
              <th scope="col" className="p-3 text-end font-extrabold text-primary">
                {t('redirects.columns.hits')}
              </th>
              <th scope="col" className="p-3 text-end font-extrabold text-primary">
                {t('list.actions')}
              </th>
            </tr>
          </thead>

          <tbody>
            {(hits.data?.data ?? []).map((row) => (
              <tr key={row.id} className="border-b border-subtle last:border-0">
                <td className="max-w-80 p-3">
                  <span className="block truncate font-semibold text-primary">{row.path}</span>
                  {row.last_seen_at ? (
                    <span className="block text-caption text-muted">
                      {new Date(row.last_seen_at).toLocaleDateString(locale)}
                    </span>
                  ) : null}
                </td>

                {/* The difference between a redirect worth writing and a bot
                    walking a list of WordPress paths. */}
                <td className="max-w-64 p-3">
                  <span className="block truncate text-caption text-secondary">
                    {row.referer ?? '—'}
                  </span>
                </td>

                <td className="p-3 text-end tabular-nums text-primary">{row.hit_count}</td>

                <td className="p-3 text-end">
                  <span className="flex flex-wrap justify-end gap-1">
                    <Button type="button" size="sm" onClick={() => onRedirect(row.path)}>
                      {t('redirects.createFor')}
                    </Button>

                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={t('redirects.dismiss', { path: row.path })}
                      disabled={dismiss.isPending}
                      onClick={() => dismiss.mutate(row.id)}
                    >
                      <Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />
                    </Button>
                  </span>
                </td>
              </tr>
            ))}

            {hits.data?.data.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-4 text-body-sm text-secondary">
                  {t('redirects.noneMissing')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
